/**
 * PlaybookPage - Searchable, browsable interface for negotiation playbook
 *
 * Features:
 * - Full-text search with debounced query
 * - Entry type filter pills
 * - Seller situation filter pills
 * - Paginated results with copy functionality
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Search,
  BookOpen,
  Phone,
  Shield,
  DollarSign,
  Target,
  Zap,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  X,
  RefreshCw,
  Filter,
  Star,
} from 'lucide-react';
import { usePlaybookSearch, usePlaybookStats } from '../hooks/usePlaybook';
import { usePlaybookFavorites } from '../hooks/usePlaybookFavorites';
import { Breadcrumbs } from '../components/Layout/Breadcrumbs';
import type { EntryType, SellerSituation, PlaybookEntry } from '../types/playbook';

// Entry type configuration
const ENTRY_TYPE_CONFIG: Record<EntryType, {
  icon: React.ElementType;
  label: string;
  color: string;
  bgColor: string;
}> = {
  opening_script: {
    icon: Phone,
    label: 'Opening Scripts',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
  },
  objection_response: {
    icon: Shield,
    label: 'Objection Responses',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
  },
  seller_finance_pitch: {
    icon: DollarSign,
    label: 'Seller Finance',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
  },
  closing_technique: {
    icon: Target,
    label: 'Closing Techniques',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
  },
  power_phrase: {
    icon: Zap,
    label: 'Power Phrases',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
  },
  transition_phrase: {
    icon: ArrowRight,
    label: 'Transitions',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
  },
  closing_phrase: {
    icon: CheckCircle,
    label: 'Closing Phrases',
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
  },
  lesson: {
    icon: Lightbulb,
    label: 'Lessons',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
  },
  motivation_signal: {
    icon: AlertCircle,
    label: 'Motivation Signals',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
  },
};

// Seller situation configuration
const SITUATION_CONFIG: Record<SellerSituation, {
  label: string;
  color: string;
}> = {
  foreclosure: { label: 'Foreclosure', color: 'bg-red-500/20 text-red-400' },
  low_equity: { label: 'Low Equity', color: 'bg-amber-500/20 text-amber-400' },
  vacant: { label: 'Vacant', color: 'bg-cyan-500/20 text-cyan-400' },
  days_on_market: { label: 'Long DOM', color: 'bg-purple-500/20 text-purple-400' },
  motivated: { label: 'Motivated', color: 'bg-emerald-500/20 text-emerald-400' },
  price_reduced: { label: 'Price Reduced', color: 'bg-blue-500/20 text-blue-400' },
  fsbo: { label: 'FSBO', color: 'bg-pink-500/20 text-pink-400' },
  expired_listing: { label: 'Expired', color: 'bg-orange-500/20 text-orange-400' },
  divorce: { label: 'Divorce', color: 'bg-violet-500/20 text-violet-400' },
  inheritance: { label: 'Inheritance', color: 'bg-teal-500/20 text-teal-400' },
  any: { label: 'Any', color: 'bg-slate-500/20 text-slate-400' },
};

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Entry card component
function PlaybookEntryCard({
  entry,
  onCopy,
  isFavorite,
  onToggleFavorite,
}: {
  entry: PlaybookEntry;
  onCopy: (text: string) => void;
  isFavorite: boolean;
  onToggleFavorite: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const config = ENTRY_TYPE_CONFIG[entry.entry_type] || ENTRY_TYPE_CONFIG.power_phrase;
  const Icon = config.icon;

  const handleCopy = () => {
    onCopy(entry.content);
    navigator.clipboard.writeText(entry.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group relative bg-slate-800/50 rounded-lg p-4 hover:bg-slate-800 transition-colors border border-slate-700/50">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${config.bgColor}`}>
            <Icon className={`w-4 h-4 ${config.color}`} />
          </div>
          <span className={`text-xs font-medium ${config.color}`}>
            {config.label}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onToggleFavorite}
            className={`p-1.5 rounded transition-colors ${
              isFavorite
                ? 'text-yellow-400 bg-yellow-500/10'
                : 'text-slate-500 hover:text-yellow-400 hover:bg-slate-700'
            }`}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
          <button
            onClick={handleCopy}
            className="p-1.5 hover:bg-slate-700 rounded transition-colors text-slate-400 hover:text-slate-200"
            title="Copy to clipboard"
          >
            {copied ? (
              <Check className="w-4 h-4 text-emerald-400" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Objection text (if applicable) */}
      {entry.objection_text && (
        <div className="text-xs text-amber-400/80 mb-2 font-medium">
          When they say: "{entry.objection_text}"
        </div>
      )}

      {/* Content */}
      <p className="text-sm text-slate-200 leading-relaxed mb-3">
        "{entry.content}"
      </p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5">
        {entry.deal_contexts.filter(c => c !== 'any').map((context) => (
          <span
            key={context}
            className="text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-400"
          >
            {context.replace('_', ' ')}
          </span>
        ))}
        {entry.seller_situations.filter(s => s !== 'any').map((situation) => {
          const sitConfig = SITUATION_CONFIG[situation as SellerSituation] || SITUATION_CONFIG.any;
          return (
            <span
              key={situation}
              className={`text-xs px-2 py-0.5 rounded ${sitConfig.color}`}
            >
              {sitConfig.label}
            </span>
          );
        })}
      </div>

      {/* Score badge */}
      {entry.effectiveness_score !== undefined && entry.effectiveness_score > 0 && (
        <div className="absolute top-4 right-14 text-xs text-slate-500">
          Score: {entry.effectiveness_score}
        </div>
      )}
    </div>
  );
}

// Filter pill component
function FilterPill({
  active,
  onClick,
  children,
  icon: Icon,
  color,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ElementType;
  color?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
        active
          ? `${color || 'bg-gold/20 text-gold'} ring-1 ring-current`
          : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
      }`}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </button>
  );
}

export function PlaybookPage() {
  // Search and filter state
  const [searchInput, setSearchInput] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<EntryType[]>([]);
  const [selectedSituations, setSelectedSituations] = useState<SellerSituation[]>([]);
  const [page, setPage] = useState(1);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Debounce search input
  const debouncedSearch = useDebounce(searchInput, 300);

  // Favorites hook
  const { favorites, toggleFavorite, isFavorite } = usePlaybookFavorites();

  // Fetch data
  const { data: stats } = usePlaybookStats();
  const {
    data: searchResults,
    isLoading,
    isFetching,
    refetch,
  } = usePlaybookSearch({
    query: debouncedSearch || undefined,
    entry_types: selectedTypes.length > 0 ? selectedTypes : undefined,
    seller_situations: selectedSituations.length > 0 ? selectedSituations : undefined,
    page,
    page_size: 20,
  });

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedTypes, selectedSituations]);

  // Toggle entry type filter
  const toggleType = useCallback((type: EntryType) => {
    setSelectedTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  }, []);

  // Toggle situation filter
  const toggleSituation = useCallback((situation: SellerSituation) => {
    setSelectedSituations((prev) =>
      prev.includes(situation)
        ? prev.filter((s) => s !== situation)
        : [...prev, situation]
    );
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchInput('');
    setSelectedTypes([]);
    setSelectedSituations([]);
    setPage(1);
    setShowFavoritesOnly(false);
  }, []);

  // Filter entries by favorites if needed
  const displayedEntries = useMemo(() => {
    if (!searchResults?.entries) return [];
    if (showFavoritesOnly) {
      return searchResults.entries.filter((entry) => favorites.has(entry.id));
    }
    return searchResults.entries;
  }, [searchResults?.entries, showFavoritesOnly, favorites]);

  // Calculate pagination
  const totalPages = searchResults ? Math.ceil(searchResults.total / searchResults.page_size) : 0;
  const hasActiveFilters = searchInput || selectedTypes.length > 0 || selectedSituations.length > 0;

  // Handle copy with optional logging
  const handleCopy = useCallback((text: string) => {
    // Could add usage logging here
    console.log('Copied script:', text.substring(0, 50) + '...');
  }, []);

  // Main entry types to show as filter pills
  const mainEntryTypes: EntryType[] = [
    'opening_script',
    'objection_response',
    'seller_finance_pitch',
    'closing_technique',
    'power_phrase',
  ];

  // Main seller situations to show as filter pills
  const mainSituations: SellerSituation[] = [
    'foreclosure',
    'vacant',
    'low_equity',
    'days_on_market',
    'motivated',
    'divorce',
    'inheritance',
  ];

  return (
    <div className="flex h-full bg-obsidian">
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex-shrink-0 px-6 py-4 border-b border-slate-700 bg-charcoal">
          <Breadcrumbs className="mb-3" />
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-gold" />
              <div>
                <h1 className="text-xl font-semibold text-ivory">
                  Negotiation Playbook
                </h1>
                <p className="text-sm text-slate-400">
                  {stats?.total_entries?.toLocaleString() || '---'} scripts and techniques
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  showFavoritesOnly
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                <Star className={`w-4 h-4 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                <span className="text-sm">Favorites ({favorites.size})</span>
              </button>
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors text-slate-400"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Search input */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search scripts, objections, phrases..."
              className="w-full pl-10 pr-10 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/50"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Entry type filters */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Entry Type
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {mainEntryTypes.map((type) => {
                const config = ENTRY_TYPE_CONFIG[type];
                return (
                  <FilterPill
                    key={type}
                    active={selectedTypes.includes(type)}
                    onClick={() => toggleType(type)}
                    icon={config.icon}
                    color={`${config.bgColor} ${config.color}`}
                  >
                    {config.label}
                  </FilterPill>
                );
              })}
            </div>
          </div>

          {/* Situation filters */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Seller Situation
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {mainSituations.map((situation) => {
                const config = SITUATION_CONFIG[situation];
                return (
                  <FilterPill
                    key={situation}
                    active={selectedSituations.includes(situation)}
                    onClick={() => toggleSituation(situation)}
                    color={config.color}
                  >
                    {config.label}
                  </FilterPill>
                );
              })}
            </div>
          </div>

          {/* Active filters indicator */}
          {hasActiveFilters && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
              <span className="text-sm text-slate-400">
                {searchResults?.total.toLocaleString() || 0} results
                {showFavoritesOnly && ` (${displayedEntries.length} favorites)`}
              </span>
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"
              >
                <X className="w-3.5 h-3.5" />
                Clear filters
              </button>
            </div>
          )}
        </header>

        {/* Results */}
        <main className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(9)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse bg-slate-800/50 rounded-lg p-4 h-40"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-slate-700 rounded" />
                    <div className="w-24 h-4 bg-slate-700 rounded" />
                  </div>
                  <div className="space-y-2">
                    <div className="w-full h-4 bg-slate-700 rounded" />
                    <div className="w-3/4 h-4 bg-slate-700 rounded" />
                    <div className="w-1/2 h-4 bg-slate-700 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : displayedEntries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <BookOpen className="w-12 h-12 text-slate-600 mb-4" />
              <h3 className="text-lg font-medium text-slate-300 mb-2">
                {showFavoritesOnly ? 'No favorites yet' : 'No results found'}
              </h3>
              <p className="text-sm text-slate-500 max-w-md">
                {showFavoritesOnly
                  ? 'Star some scripts to add them to your favorites.'
                  : 'Try adjusting your search or filters to find what you\'re looking for.'}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-sm text-gold hover:text-gold/80"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {displayedEntries.map((entry) => (
                  <PlaybookEntryCard
                    key={entry.id}
                    entry={entry}
                    onCopy={handleCopy}
                    isFavorite={isFavorite(entry.id)}
                    onToggleFavorite={() => toggleFavorite(entry.id)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && !showFavoritesOnly && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700">
                  <span className="text-sm text-slate-400">
                    Page {page} of {totalPages.toLocaleString()}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors text-slate-300"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors text-slate-300"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </main>

        {/* Footer attribution */}
        <footer className="flex-shrink-0 px-6 py-3 border-t border-slate-700 bg-charcoal">
          <p className="text-xs text-slate-500 text-center">
            Extracted from @hold_my_hand_wholesale video content
          </p>
        </footer>
      </div>
    </div>
  );
}

export default PlaybookPage;
